import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {Machine, PipelineState} from "../model/machine.model";
import {ViewManagerService} from "./view-manager.service";
import {Level, Message} from "../model/message.component";
import {map} from "rxjs/operators";
import {ObjectMapper} from "json-object-mapper";
import {Channel} from "../model/channels.model";
import {BehaviorSubject} from "rxjs";
import {ResultCase} from "../model/lookup-results.model";

@Injectable({
  providedIn: 'root'
})
export class MachineManagerService {

  machine: Machine

  pipelinesSubject = new BehaviorSubject<PipelineState[]>([]);

  constructor(
    private http: HttpClient,
    private viewManager: ViewManagerService
  ) {
  }

  load(owner: String) {
    this.http.get<Machine>(environment.batchPrefix + "/machine/status/" + owner)
      .pipe()
      .subscribe({
        next: (result) => {
          this.machine = result;
          this.pipelinesSubject.next(result.pipelines);
        },
        error: error => {
          this.pipelinesSubject.next([]);
          this.viewManager.displayMessage(new Message(error.error, new Level("error")))
        }
      });
  }

  reload(owner: String) {
    this.http.post<any>(environment.apiPrefix + "/reload/" + owner, {})
      .subscribe({
        next: (result) => {
          this.viewManager.displayMessage(new Message("Nodes reload result: " + result['result'], new Level("info")))
        },
        error: error => {
          this.viewManager.displayMessage(new Message(error.error, new Level("error")))
        }
      });
  }

  set(owner: String, batch: String) {
    this.http.post<Machine>(environment.batchPrefix + "/machine/set", {"owner": owner, "batch": batch})
      .pipe(map(result => ObjectMapper.deserialize(Machine, result)))
      .subscribe({
        next: (result) => {
          if (result) {
            this.machine = result;
            this.pipelinesSubject.next(result.pipelines);
          } else {
            this.pipelinesSubject.next([]);
            this.viewManager.displayMessage(new Message("machine set, operation failed", new Level("warning")));
          }
        },
        error: error => {
          this.viewManager.displayMessage(new Message("machine set, operation failed: " + error.error, new Level("error")));
        }
      });
  }

  pipelineRun(batch: String, ps: PipelineState) {
    this.http.post<string>(environment.batchPrefix + "/start", {"id": batch, "channel": ps.channel})
      .subscribe({
        next: (result) => {
          if (result) {
            ps.status = result;
            this.viewManager.displayMessage(new Message("batch started, operation status: " + result, new Level("info")));
          } else {
            this.viewManager.displayMessage(new Message("batch start, operation failed", new Level("warning")));
          }
        },
        error: error => {
          this.viewManager.displayMessage(new Message("machine start, operation failed: " + error.error, new Level("error")));
        }
      });
  }

  pipelineCancel(channel: Channel) {
    this.http.post(environment.batchPrefix + "/stop", channel)
      .subscribe({
        next: (result) => {
          if (result) {
            this.viewManager.displayMessage(new Message("batch stopped, operation status: " + result, new Level("info")));
          } else {
            this.viewManager.displayMessage(new Message("batch stop, operation failed", new Level("warning")));
          }
        },
        error: error => {
          this.viewManager.displayMessage(new Message("machine stop, operation failed: " + error.error, new Level("error")));
        }
      });
  }

  realtimeRun(batch: String, channel: Channel) {
    this.http.post<string>(environment.realtimePrefix + "/start", {"id": batch, "channel": channel})
      .subscribe({
        next: (result) => {
          if (result) {
            this.viewManager.displayMessage(new Message("realtime started, operation status: " + result, new Level("info")));
          } else {
            this.viewManager.displayMessage(new Message("realtime start, operation failed", new Level("warning")));
          }
        },
        error: error => {
          this.viewManager.displayMessage(new Message("machine start, operation failed: " + error.error, new Level("error")));
        }
      });
  }

  realtimeCancel(channel: Channel) {
    this.http.post(environment.realtimePrefix + "/stop", channel)
      .subscribe({
        next: (result) => {
          if (result) {
            this.viewManager.displayMessage(new Message("realtime stopped, operation status: " + result, new Level("info")));
          } else {
            this.viewManager.displayMessage(new Message("realtime stop, operation failed", new Level("warning")));
          }
        },
        error: error => {
          this.viewManager.displayMessage(new Message("machine stop, operation failed: " + error.error, new Level("error")));
        }
      });
  }
}
